import { RankTier } from '@montugroup/pms-api-contracts';
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';
import { Alert, Card, IconButton, Stack } from '@mui/material';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import type {
  Attribute,
  FilteredProduct,
  FilteredProductByFormulation,
  ProductFilter as ProductFilterType,
  RankDecoration,
} from '@/api';
import { isRankDecoration, isStockDecoration } from '@/api';
import { ProductFilter } from '@/shared-ui';
import type { RadioOption } from '@/shared-ui/FeedbackRadioGroup/FeedbackRadioGroup';
import type { Filters } from '@/shared-ui/ProductFilter/ProductFilter';
import ProductSelection from '@/shared-ui/ProductSelection/ProductSelection';
import { useAppStore } from '@/state-management';
import { Logger } from '@/utils/logger';

import type { MedicationPadItemType } from '../PrescriptionPad';
import { getAnswerDisplayPositions } from '../PrescriptionPad';
import {
  PRODUCT_INTERVAL_DEFAULT,
  PRODUCT_QUANTITY_DEFAULT,
  PRODUCT_REPEATS_DEFAULT,
} from '../ProductDetailsCard/ProductDetailsCard.constants';
import type { ProductSelectionFeedbackProps } from '../ProductSelectionFeedback/ProductSelectionFeedback';
import ProductSelectionFeedback from '../ProductSelectionFeedback/ProductSelectionFeedback';

import { FF_PAT_PRESCRIPTION_DOSAGE_MIN_LENGTH_VALIDATION } from '@/constants/featureFlags';
import { useFeatureFlags } from '@/hooks';
import { useGetProductSurvey } from '@/hooks/rest/useGetProductSurvey';
import { DEFAULT_QUESTION_ID, REISSUED_ANSWER_ID } from '@/shared-ui/TierLabel/TierLabel.types';
import type { PrescriptionPadItemDetailType } from './PrescriptionItemEditor.types';
import { PrescriptionPadItemDetail } from './PrescriptionItemEditor.types';
import { PrescriptionItemAlert } from './components/PrescriptionItemAlert';
import { PrescriptionItemAttributesEditor } from './components/PrescriptionItemAttributesEditor';
import { RecommendationInfoAlert } from './components/RecommendationInfoAlert';
import { useFilteredMedicationOptions, usePrescriptionItemFeatureFlags } from './hooks';

const logger = new Logger('PrescriptionItemEditor');

export type MedicationPadItemEditorProps = {
  /**
   * Unique identifier to find the prescription item that matches the ones in Prescription Provider
   */
  medicationItemId: string;
  /**
   * Doctor's ID used to fetch the product survey data
   * TODO: this can eventually be retrieved from zustand slice as part of future yard work (B2C-8436)
   */
  doctorId: number;
  /**
   * All the available filters
   * TODO: review whether we get this directly from react-query, including the feedback props
   */
  filterDataSource: ProductFilterType;
  medicationDataSource: FilteredProductByFormulation;
  /**
   * Invoked whenever the medication, dosage, interval and repeats etc. is changed.
   */
  onMedicationPadItemChange?: (item: MedicationPadItemType) => void;
  onPreviewIconClick: (item: FilteredProduct) => void;
};

/**
 * Used to edit a prescription item, including medication selection, dosage and interval.
 * This component relies on the `Prescription` provider and will not work without it.
 */
export const MedicationPadItemEditor = ({
  medicationItemId,
  doctorId,
  filterDataSource,
  medicationDataSource,
  onPreviewIconClick,
}: MedicationPadItemEditorProps) => {
  const { flags } = useFeatureFlags();
  const ffPatPrescriptionDosageMinLengthValidation = flags[FF_PAT_PRESCRIPTION_DOSAGE_MIN_LENGTH_VALIDATION];

  const { recommendationInfo, recommendedMedicationsCount } = usePrescriptionItemFeatureFlags();
  const {
    prescriptionPad: { medicationPadItems },
  } = useAppStore.use.prescriptionAssistant();
  const updateMedicationPadItemById = useAppStore.use.updateMedicationPadItemById();
  const removeMedicationPadItemById = useAppStore.use.removeMedicationPadItemById();

  const medicationPadItem = useMemo(() => {
    return (
      medicationPadItems.find((existingItem) => existingItem.medicationPadItemId === medicationItemId) || {
        medicationPadItemId: medicationItemId,
      }
    );
  }, [medicationPadItems, medicationItemId]);

  const {
    /**
     * ID that identifies this current medication pad item
     */
    medicationPadItemId: prescriptionItemId,
    /**
     * Item's detail, including medication (product), dosage, interval etc.
     */
    medicationPadItem: medicationPadItemDetail,
    reissueStatus,
    filters: prescriptionItemFilters,
    moreProductsLoaded,
  } = medicationPadItem;

  const [isMedicationSelectionOpen, setIsMedicationSelectionOpen] = useState(false);
  const [showViewMoreProducts, setShowViewMoreProducts] = useState(true);
  /**
   * The initial reference used within PrescriptionItemAlert component.
   * When alertProductId is changed, PrescriptionItemAlert restarts all of its internal logic to make sure it's up to date.
   */
  const [alertProductId, setAlertProductId] = useState<number | undefined>(medicationPadItemDetail?.productId);

  const initialReissueMedication = useRef<FilteredProduct | undefined>(undefined);

  const {
    selectedFilters,
    medicationOptions,
    filteredMedicationOptions,
    calculateMedicationOptionsFromFilters,
    isReady,
  } = useFilteredMedicationOptions({
    initialFilters: prescriptionItemFilters,
    medicationDataSource,
    displayAllOptions: !showViewMoreProducts || moreProductsLoaded,
    prescribedMedicationId: medicationPadItemDetail?.productId,
    initialReissueMedication: initialReissueMedication.current,
  });

  // Generate product survey feedback props

  const { data: productSurveyData } = useGetProductSurvey(doctorId);
  const firstQuestion = productSurveyData?.survey?.[0]?.question ?? {
    value: 0,
    name: '',
    subText: '',
    answers: [],
  };
  // TODO: this can be potentially moved directly in the component that uses it (ProductSelectionFeedback)
  const productSelectionFeedbackProps: ProductSelectionFeedbackProps = {
    label: 'Show more products',
    subText: firstQuestion?.subText,
    questionId: firstQuestion?.value,
    title: firstQuestion?.name,
    answers: firstQuestion?.answers,
  };

  /**
   * Updates the medicationPadItem in the app store.
   * Handles setting alert product id if not set.
   */
  const updateMedicationPadItemInStore = useCallback(
    (medicationPadItem: MedicationPadItemType) => {
      updateMedicationPadItemById(medicationPadItem, {
        ffPatPrescriptionDosageMinLengthValidation,
      });
      if (alertProductId === undefined) {
        setAlertProductId(medicationPadItem.medicationPadItem?.productId);
      }
    },
    [alertProductId, ffPatPrescriptionDosageMinLengthValidation, updateMedicationPadItemById],
  );

  const validateAndUpdateMedicationPadItemDetail = useCallback(
    (partialDetail: Partial<PrescriptionPadItemDetailType>) => {
      if (!medicationPadItemDetail && !partialDetail?.productId && !partialDetail?.productName) {
        return;
      }
      const updatedPrescriptionItemDetail: PrescriptionPadItemDetailType = {
        ...medicationPadItemDetail,
        ...partialDetail,
      } as PrescriptionPadItemDetailType; // assertion is safe, as the conditional above will early return if required props don't exit.

      const validation = PrescriptionPadItemDetail.safeParse(updatedPrescriptionItemDetail);
      if (!validation.success) {
        logger.error('validateAndUpdatePrescriptionItemDetail: invalid prescription details used', {
          updatedPrescriptionItemDetail,
        });
        return;
      }
      return updatedPrescriptionItemDetail;
    },
    [medicationPadItemDetail],
  );

  const getPrescriptionItemDetailFromMedication = (
    medication: FilteredProduct,
  ): PrescriptionPadItemDetailType | undefined => {
    const updatedPrescriptionItemDetail = validateAndUpdateMedicationPadItemDetail({
      productId: medication.id,
      productName: medication.productName,
      supplierName: medication.supplierName,
      dosage: medication.attributes?.dosage,
    });

    return updatedPrescriptionItemDetail;
  };

  /**
   * Currently selected medication derived from the context
   */
  const selectedMedication = useMemo(() => {
    if (filteredMedicationOptions.length === 0) {
      return undefined;
    }
    // if nothing is prescribed yet, but there are options available, set the first one
    if (!medicationPadItemDetail?.productId) {
      return filteredMedicationOptions[0];
    }

    const matchedMedicationInOptions = filteredMedicationOptions.find(
      (medication) => medication.id === medicationPadItemDetail?.productId,
    );
    return matchedMedicationInOptions || filteredMedicationOptions[0];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filteredMedicationOptions, medicationPadItemDetail?.productId]);

  const isInvalidSelection = selectedMedication ? isStockDecoration(selectedMedication.decoration) : false;

  useEffect(() => {
    if (reissueStatus?.isReissued && !initialReissueMedication.current && selectedMedication) {
      initialReissueMedication.current = selectedMedication;
    }
  }, [reissueStatus?.isReissued, selectedMedication]);

  /**
   * this effect is specifically for handling out of stock medication
   * and whether to dispatch the appropriate first option to the state management or not
   */
  useEffect(() => {
    if (!selectedMedication) {
      return;
    }

    if (filteredMedicationOptions.length === 0) {
      return;
    }

    if (isStockDecoration(selectedMedication.decoration)) {
      if (initialReissueMedication.current?.id === selectedMedication.id) {
        return;
      }
      // when it's out of stock, go for the first option in the list. but if even that option is out of stock, the exit and don't dispatch
      if (
        isStockDecoration(filteredMedicationOptions[0].decoration) &&
        filteredMedicationOptions[0].decoration.isOutOfStock
      ) {
        return;
      }

      // if it's out of stock, don't clear the reissue status if it exists
      const updatedPrescriptionItemDetail = getPrescriptionItemDetailFromMedication(filteredMedicationOptions[0]);
      if (!updatedPrescriptionItemDetail) {
        return;
      }

      const isOutsideOfTopRecommendation =
        (filteredMedicationOptions[0]?.decoration as RankDecoration)?.tier !== RankTier.First;

      const updatedPrescriptionItem: MedicationPadItemType = {
        ...medicationPadItem,
        moreProductsLoaded: isOutsideOfTopRecommendation,
        feedback: isOutsideOfTopRecommendation
          ? {
              questionAsked: DEFAULT_QUESTION_ID,
              answerSelected: REISSUED_ANSWER_ID,
            }
          : undefined,
        medicationPadItem: updatedPrescriptionItemDetail,
        filters: selectedFilters,
      };
      updateMedicationPadItemInStore(updatedPrescriptionItem);
      // update alert's initial product id reference to make sure logic is up to date.
      setAlertProductId(updatedPrescriptionItem.medicationPadItem?.productId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedMedication, filteredMedicationOptions.length, alertProductId]);

  /**
   * this effect is specifically used to clear the medication selection from state management,
   * when no available medication options are there to be selected
   */
  useEffect(() => {
    if (!isReady) {
      return;
    }

    if (filteredMedicationOptions.length > 0) {
      return;
    }
    const updatedPrescriptionItem: MedicationPadItemType = {
      ...medicationPadItem,
      medicationPadItem: undefined,
      filters: selectedFilters,
    };
    updateMedicationPadItemInStore(updatedPrescriptionItem);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filteredMedicationOptions.length, isReady]);

  const handleFilterChanged = (changedFilters: Filters) => {
    calculateMedicationOptionsFromFilters(changedFilters);
  };

  const handleMedicationChange = useCallback(
    (changedSelection: FilteredProduct) => {
      // validate - move this part to shared util function
      if (changedSelection.id === medicationPadItemDetail?.productId) {
        // same thing, don't dispatch anything
        return;
      }
      // If the user is changing a reissued medication, move it to cancel

      // validate for OOS - clear out previous selection if OOS selected.

      const { id, productName, supplierName, attributes } = changedSelection;
      const updatedPrescriptionItemDetail = validateAndUpdateMedicationPadItemDetail({
        ...medicationPadItemDetail,
        productId: id,
        productName: productName,
        supplierName: supplierName,
        repeats: attributes?.repeats?.default ?? PRODUCT_REPEATS_DEFAULT,
        interval: attributes?.interval?.default ?? PRODUCT_INTERVAL_DEFAULT,
        quantity: attributes?.quantity?.default ?? PRODUCT_QUANTITY_DEFAULT,
        dosage: attributes?.dosage ?? '',
        dispenseSize: attributes?.dispenseSize,
      });

      if (!updatedPrescriptionItemDetail) {
        logger.error('handleMedicationChange: validation failed', {
          updatedPrescriptionItemDetail,
        });
        return;
      }

      // check for ranking tiers and feedback
      const isTopRecommended =
        isRankDecoration(changedSelection.decoration) && changedSelection.decoration.tier === RankTier.First;

      const isInitialMedication = initialReissueMedication.current?.id === changedSelection.id;
      let feedback = medicationPadItem.feedback ? { ...medicationPadItem.feedback } : undefined;

      if ((isTopRecommended || isInitialMedication) && !reissueStatus) {
        feedback = undefined;
        setShowViewMoreProducts(true);
      }

      const updatedPrescriptionItem: MedicationPadItemType = {
        ...medicationPadItem,
        medicationPadItem: updatedPrescriptionItemDetail,
        filters: selectedFilters,
        feedback,
      };
      updateMedicationPadItemInStore(updatedPrescriptionItem);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [reissueStatus, medicationPadItem, selectedFilters, alertProductId],
  );

  const handleAttributesChange = (attribute: Attribute, value: string | number) => {
    if (!medicationPadItemDetail) {
      return;
    }
    const updatedPrescriptionItemDetail = validateAndUpdateMedicationPadItemDetail({
      ...medicationPadItemDetail,
      [attribute]: value,
    });

    if (!updatedPrescriptionItemDetail) {
      logger.error('handleAttributeChange: validation failed', {
        updatedPrescriptionItemDetail,
      });
      return;
    }

    const updatedPrescriptionItem: MedicationPadItemType = {
      ...medicationPadItem,
      medicationPadItem: updatedPrescriptionItemDetail,
    };
    updateMedicationPadItemInStore(updatedPrescriptionItem);
  };

  const handleSurveyFeedbackChange = (answerId: number, randomAnswers: RadioOption[]) => {
    setIsMedicationSelectionOpen(true);
    setShowViewMoreProducts(false);
    if (answerId < 1) {
      logger.error('handleFeedbackChange:invalid answer id', {
        answerId,
      });
      return;
    }
    const updatedPrescriptionItem: MedicationPadItemType = {
      ...medicationPadItem,
      moreProductsLoaded: true,
      feedback: {
        questionAsked: productSelectionFeedbackProps.questionId,
        answerSelected: answerId,
        answerDisplayPositions: getAnswerDisplayPositions(productSelectionFeedbackProps.answers, randomAnswers),
      },
    };
    updateMedicationPadItemInStore(updatedPrescriptionItem);
  };

  const handlePreviewIconClick = (medication: FilteredProduct) => {
    setIsMedicationSelectionOpen(false);
    onPreviewIconClick(medication);
  };

  // keep the menu list open when feedback has been selected. This ensures that `ProductSelection` is only "controlled" when `isMedicationSelectionOpen` is true
  const keepProductSelectionOpen = isMedicationSelectionOpen ? { open: true } : null;
  const surveyFeedbackElement = [
    showViewMoreProducts && !moreProductsLoaded && medicationOptions.length > recommendedMedicationsCount && (
      <ProductSelectionFeedback
        key={`productSelectFeedback-${prescriptionItemId}`}
        {...productSelectionFeedbackProps}
        onConfirm={handleSurveyFeedbackChange}
      />
    ),
    medicationOptions.length < recommendedMedicationsCount && (
      <Alert key={`all-product-shown-${prescriptionItemId}`} severity="info">
        All products are showing
      </Alert>
    ),
  ];

  return (
    <Card aria-label="Medication selector" raised>
      <Stack direction="row" padding={7}>
        <Stack minWidth="90%" spacing={6}>
          {alertProductId && <PrescriptionItemAlert medicationItemId={medicationItemId} productId={alertProductId} />}

          <ProductFilter
            selectedFilters={selectedFilters}
            productFilter={filterDataSource}
            onChange={handleFilterChanged}
          />
          {/* Recommendation Info */}
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <RecommendationInfoAlert info={recommendationInfo} />
          </Stack>
          {/* Medication Selection */}
          {!!medicationOptions?.length && (
            <>
              <ProductSelection
                {...keepProductSelectionOpen}
                value={selectedMedication}
                options={filteredMedicationOptions}
                onChange={handleMedicationChange}
                onClose={() => setIsMedicationSelectionOpen(false)}
                onPreviewIconClick={handlePreviewIconClick}
                MenuProps={{
                  sx: {
                    ['& .MuiSvgIcon-root']: {
                      color: 'orange.main',
                    },
                    overflowY: 'scroll',
                  },
                }}
                bottomElement={surveyFeedbackElement}
                enabledMedicationId={initialReissueMedication.current?.id ?? selectedMedication?.id}
                error={isInvalidSelection}
              />
              <PrescriptionItemAttributesEditor
                quantity={medicationPadItemDetail?.quantity ?? PRODUCT_QUANTITY_DEFAULT}
                interval={medicationPadItemDetail?.interval ?? PRODUCT_INTERVAL_DEFAULT}
                repeats={medicationPadItemDetail?.repeats ?? PRODUCT_REPEATS_DEFAULT}
                dosage={medicationPadItemDetail?.dosage ?? ''}
                selectedMedication={selectedMedication}
                onChange={handleAttributesChange}
              />
            </>
          )}
        </Stack>

        {/* Delete Button */}
        <Stack direction="row" alignItems="flex-end" justifyContent="flex-end" spacing={2} width="100%">
          <IconButton
            aria-label="remove"
            onClick={() => removeMedicationPadItemById(prescriptionItemId)}
            sx={{ padding: 0 }}
          >
            <DeleteOutlineOutlinedIcon fontSize="large" />
          </IconButton>
        </Stack>
      </Stack>
    </Card>
  );
};
